"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "@/components/motion/framer"
import { ProjectTypeSelector } from "./ProjectTypeSelector"
import { ConditionalQuestions } from "./ConditionalQuestions"
import { AdditionalInfo } from "./AdditionalInfo"
import { ContactForm } from "./ContactForm"
import { SummaryView } from "./SummaryView"
import { ProgressIndicator } from "./ProgressIndicator"
import { Typography } from "@/components/typography"
import { getAttribution, trackEvent } from "@/lib/attribution"

const STORAGE_KEY = "tallkarol_contact_form_state"

const STEP_NAMES: Record<number, string> = {
  1: "project_type",
  2: "details",
  3: "additional",
  4: "summary",
  5: "contact",
}

export type ProjectType = "website" | "app" | "tool" | "design" | "integrations" | "consulting" | "unknown"

export interface ProjectConfig {
  projectTypes: ProjectType[]
  website?: {
    type?: string
    currentSituation?: string
    design?: string
    painPoints?: string[]
    features?: string[]
  }
  app?: {
    type?: string
    purpose?: string
    users?: string
    painPoints?: string[]
  }
  tool?: {
    type?: string
    painPoints?: string[]
  }
  design?: {
    type?: string
    currentState?: string
    painPoints?: string[]
  }
  integrations?: {
    systems?: string[]
    painPoints?: string[]
    goal?: string
  }
  consulting?: {
    type?: string
    currentSituation?: string
    painPoints?: string[]
    goals?: string[]
  }
  unknown?: {
    whatsNotWorking?: string
    tryingToAchieve?: string
    painPoints?: string[]
  }
  additional?: {
    timeline?: string
    budget?: string
    anythingElse?: string
  }
  contact?: {
    name?: string
    email?: string
    company?: string
  }
}

export function ProjectConfigurator() {
  // Load state from localStorage on mount
  const loadSavedState = () => {
    if (typeof window === "undefined") {
      return { step: 1, config: { projectTypes: [] } }
    }
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        // Only restore if it's recent (within 24 hours)
        if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          return { step: parsed.step || 1, config: parsed.config || { projectTypes: [] } }
        }
      }
    } catch (e) {
      console.error("Failed to load saved state:", e)
    }
    return { step: 1, config: { projectTypes: [] } }
  }

  const savedState = loadSavedState()
  const [step, setStep] = useState(savedState.step)
  const [config, setConfig] = useState<ProjectConfig>(savedState.config)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle")
  const [formStarted, setFormStarted] = useState(false)

  useEffect(() => {
    trackEvent("form_step", {
      form_id: "contact_configurator",
      form_step: step,
      step_name: STEP_NAMES[step] || String(step),
    })
  }, [step])

  const markFormStart = () => {
    if (formStarted) return
    setFormStarted(true)
    trackEvent("form_start", { form_id: "contact_configurator" })
  }

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== "undefined" && submitStatus !== "success") {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            step,
            config,
            timestamp: Date.now(),
          })
        )
      } catch (e) {
        console.error("Failed to save state:", e)
      }
    }
  }, [step, config, submitStatus])

  // Clear saved state on successful submission
  useEffect(() => {
    if (submitStatus === "success" && typeof window !== "undefined") {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [submitStatus])

  const updateConfig = (updates: Partial<ProjectConfig>) => {
    setConfig((prev) => {
      // Create a completely new object to ensure React detects the change
      const newConfig = { ...prev }
      // For nested objects, ensure we create new references
      Object.keys(updates).forEach((key) => {
        const k = key as keyof ProjectConfig
        if (updates[k] !== undefined) {
          newConfig[k] = updates[k] as any
        }
      })
      return newConfig
    })
  }

  // Determine which steps are accessible for navigation
  const getAccessibleSteps = (): number[] => {
    const accessible: number[] = []
    
    // Step 1 is always accessible
    accessible.push(1)
    
    // Step 2 requires at least one project type
    if (config.projectTypes.length > 0) {
      accessible.push(2)
      
      // Step 3 is accessible once step 2 is completed (user has navigated through step 2)
      if (step >= 3) {
        accessible.push(3)
        
        // Step 4 (summary) is accessible once step 3 is reached
        if (step >= 4) {
          accessible.push(4)
          
          // Step 5 (contact form) is accessible once step 4 is reached
          if (step >= 5) {
            accessible.push(5)
          }
        }
      }
    }
    
    return accessible
  }

  const handleStepClick = (targetStep: number) => {
    // Always allow backward navigation
    if (targetStep < step) {
      setStep(targetStep)
      return
    }
    
    // Forward navigation requires validation
    const accessibleSteps = getAccessibleSteps()
    if (accessibleSteps.includes(targetStep)) {
      setStep(targetStep)
    }
  }

  const handleNext = () => {
    if (step === 1 && config.projectTypes.length === 0) {
      return // Can't proceed without selecting at least one project type
    }
    setStep((prev: number) => Math.min(prev + 1, 5))
  }

  const handleBack = () => {
    setStep((prev: number) => Math.max(prev - 1, 1))
  }

  const handleProjectTypesChange = (types: ProjectType[]) => {
    markFormStart()
    updateConfig({ projectTypes: types })
  }

  const handleSubmit = async (contactInfo: { name: string; email: string; company?: string }) => {
    updateConfig({ contact: contactInfo })
    setIsSubmitting(true)
    setSubmitStatus("idle")
    
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          config,
          contact: contactInfo,
          attribution: getAttribution(),
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || "Failed to send message")
      }

      setSubmitStatus("success")
      // Clear saved state and reset form after 3 seconds
      if (typeof window !== "undefined") {
        localStorage.removeItem(STORAGE_KEY)
      }
      setTimeout(() => {
        setStep(1)
        setConfig({ projectTypes: [] })
        setSubmitStatus("idle")
      }, 3000)
    } catch (error) {
      console.error("Submission error:", error)
      setSubmitStatus("error")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div id="intake" className="w-full max-w-4xl mx-auto relative z-10 scroll-mt-24">
      <ProgressIndicator 
        currentStep={step} 
        totalSteps={5}
        onStepClick={handleStepClick}
        accessibleSteps={getAccessibleSteps()}
      />
      
      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-8">
              <Typography variant="h2" as="h2" className="mb-4">
                What are you looking for?
              </Typography>
              <Typography variant="body" className="text-tk-slate/80">
                Select all that apply. You can choose multiple project types.
              </Typography>
            </div>
            <ProjectTypeSelector
              selectedTypes={config.projectTypes}
              onChange={handleProjectTypesChange}
              onNext={handleNext}
            />
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <ConditionalQuestions
              projectTypes={config.projectTypes}
              config={config}
              onUpdate={updateConfig}
              onNext={handleNext}
              onBack={handleBack}
            />
          </motion.div>
        )}

        {step === 3 && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <AdditionalInfo
              config={config}
              onUpdate={updateConfig}
              onNext={handleNext}
              onBack={handleBack}
            />
          </motion.div>
        )}

        {step === 4 && (
          <motion.div
            key="step4"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <SummaryView
              config={config}
              onBack={handleBack}
              onNext={handleNext}
            />
          </motion.div>
        )}

        {step === 5 && (
          <motion.div
            key="step5"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <ContactForm
              onSubmit={handleSubmit}
              onBack={handleBack}
              isSubmitting={isSubmitting}
              submitStatus={submitStatus}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

